import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function DogFriendlyDiningVancouver() {
  const ref_id = "dog-friendly-dining-vancouver";
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  const labelsMap = {
    intro: "Introduction",
    "west-van": "West Vancouver's Pet-Friendly Gems",
    "downtown": "Downtown Hotspots",
    "breweries": "Breweries & Casual Eateries",
    "waterfront": "Waterfront Dining",
    "regulations": "Local Regulations",
    "faq": "FAQ",
    "conclusion": "Conclusion",
    "references": "References"
  };

  const referencesData = [
    {
      url: "https://www.bringfido.ca/restaurant/city/vancouver_bc_ca/",
      label: "BringFido Vancouver Guide"
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Tourism Vancouver"
    }
  ];

  const locations = [
    {
      id: "west-van",
      lat: 49.33125,
      lng: -123.16875,
      popupText: "The Red Lion Bar & Grill"
    },
    {
      id: "downtown",
      lat: 49.28395,
      lng: -123.10859,
      popupText: "Gringo Gastown"
    },
    {
      id: "breweries",
      lat: 49.27345,
      lng: -123.09876,
      popupText: "Red Truck Beer Company"
    },
    {
      id: "waterfront",
      lat: 49.27563,
      lng: -123.14456,
      popupText: "Go Fish"
    }
  ];

  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  const mapRef = useRef(null);
  const mobileMapRef = useRef(null);

  return (
    <>
      <Helmet>
        <title>Dog-Friendly Dining in Vancouver | Patio Guide</title>
        <meta name="description" content="Complete guide to Vancouver's best dog-friendly restaurants, breweries, and waterfront dining spots with coordinates and local regulations." />
        <meta name="keywords" content="dog friendly restaurants Vancouver, pet dining, outdoor patios, breweries" />
        <link rel="canonical" href="https://www.bcouver.com/articles/dog-friendly-dining-vancouver" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="Top Dog-Friendly Restaurants in Vancouver" />
        <meta property="og:description" content="Your guide to dining with dogs in Vancouver, featuring waterfront patios, craft breweries, and local regulations." />
        <meta property="og:image" content="https://bcouver.com/assets/images/dog-friendly-dining-vancouver.jpg" />

        {/* Twitter */}
        <meta name="twitter:title" content="Vancouver's Best Dog-Friendly Dining Spots" />
        <meta name="twitter:description" content="Discover 10+ pet-welcoming restaurants with patio coordinates and local tips." />
      </Helmet>

      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          <article className="article-content">
            <img
              src="/assets/images/dog-friendly-dining-vancouver.jpg"
              alt="Dogs enjoying patio dining in Vancouver"
              className="featured-image"
            />

            <SocialMedia/>

            <h2 id="intro">Introduction</h2>
            <p>Vancouver's culinary scene offers dozens of dog-friendly options ranging from waterfront patios to craft breweries. This guide covers top spots with coordinates, regulations, and local tips.</p>

            <h2 id="west-van">West Vancouver's Pet-Friendly Gems</h2>
            <p ref={(el) => setSpotRef("west-van", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>The Red Lion Bar & Grill</strong>{" "}
              <Marker coordY={49.33125} coordX={-123.16875} mobileMapRef={mobileMapRef} mapRef={mapRef} />
              - British-style pub with heated patio and water bowls for dogs.
            </p>
            <p>Features include live music, stone fireplace, and classic pub fare.</p>

            <h2 id="downtown">Downtown Hotspots</h2>
            <p ref={(el) => setSpotRef("downtown", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Gringo Gastown</strong>{" "}
              <Marker coordY={49.28395} coordX={-123.10859} mobileMapRef={mobileMapRef} mapRef={mapRef} />
              - Tex-Mex favorite with heated patio and dog treats.
            </p>

            <h2 id="breweries">Breweries & Casual Eateries</h2>
            <p ref={(el) => setSpotRef("breweries", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Red Truck Beer Company</strong>{" "}
              <Marker coordY={49.27345} coordX={-123.09876} mobileMapRef={mobileMapRef} mapRef={mapRef} />
              - Retro brewery allowing dogs indoors and outdoors.
            </p>

            <h2 id="waterfront">Waterfront Dining</h2>
            <p ref={(el) => setSpotRef("waterfront", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Go Fish</strong>{" "}
              <Marker coordY={49.27563} coordX={-123.14456} mobileMapRef={mobileMapRef} mapRef={mapRef} />
              - Seaside fish shack with picnic tables along False Creek.
            </p>

            <h2 id="regulations">Local Regulations</h2>
            <ul>
              <li>Dogs must remain leashed on all patios</li>
              <li>Service animals only allowed indoors</li>
              <li>Best visiting hours: Weekdays 2-4 PM</li>
            </ul>

            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>Can dogs sit inside restaurants?</h3>
              <p>Only certified service animals are permitted indoors per health regulations.</p>
              <h3>Do any spots offer dog menus?</h3>
              <p>Uncle Abe's provides a dedicated dog menu with turkey bacon strips.</p>
            </div>

            <h2 id="conclusion">Conclusion</h2>
            <p>From mountain-view patios to harbor-side seafood shacks, Vancouver offers diverse dog-friendly dining options for every taste.</p>

            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          <Right currentArticle={ref_data} />
        </div>
      </main>

      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}
